import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Modal from "react-bootstrap/Modal";
import { AuthContext } from "../context/auth";

// Tiempo de inactividad (en segundos) sin teclado ni ratón antes de
// mostrar el aviso de extensión de sesión. Ajustar aquí si se desea.
const TIEMPO_INACTIVIDAD_AVISO = 300; // 5 min

// Tiempo (en segundos) que tiene el usuario para pulsar "Extender sesión"
// antes de que la sesión se cierre automáticamente.
const TIEMPO_CUENTA_REGRESIVA = 30;

const SessionTimeout = () => {
  const { user, logout } = useContext(AuthContext);
  const history = useHistory();

  const [mostrarAviso, setMostrarAviso] = useState(false);
  const [segundosRestantes, setSegundosRestantes] = useState(
    TIEMPO_CUENTA_REGRESIVA
  );

  const segundosInactividad = useRef(0);
  const fechaLimite = useRef(null);
  const timerInactividad = useRef(null);
  const timerCuentaRegresiva = useRef(null);

  // Refs para evitar usar valores obsoletos dentro de los listeners/timers.
  const userRef = useRef(user);
  const mostrarAvisoRef = useRef(mostrarAviso);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    mostrarAvisoRef.current = mostrarAviso;
  }, [mostrarAviso]);

  // Al cerrarse la sesión (usuario null) se reinicia todo el estado del
  // vigilante para que al volver a ingresar no aparezca el aviso de inmediato.
  useEffect(() => {
    if (!user) {
      segundosInactividad.current = 0;
      fechaLimite.current = null;
      setSegundosRestantes(TIEMPO_CUENTA_REGRESIVA);
      setMostrarAviso(false);
    }
  }, [user]);

  const cerrarSesion = () => {
    if (!userRef.current) return;
    segundosInactividad.current = 0;
    fechaLimite.current = null;
    setSegundosRestantes(TIEMPO_CUENTA_REGRESIVA);
    setMostrarAviso(false);
    logout();
    history.push("/");
  };

  const extenderSesion = () => {
    segundosInactividad.current = 0;
    fechaLimite.current = null;
    setSegundosRestantes(TIEMPO_CUENTA_REGRESIVA);
    setMostrarAviso(false);
  };

  const reiniciarInactividad = () => {
    // Mientras el aviso está visible, sólo el botón "Extender sesión"
    // reinicia el contador.
    if (!userRef.current || mostrarAvisoRef.current) return;
    segundosInactividad.current = 0;
  };

  // Contador de inactividad, activo únicamente con sesión iniciada.
  useEffect(() => {
    if (!user) return undefined;

    const eventos = [
      "mousemove",
      "mousedown",
      "keydown",
      "touchstart",
      "click",
      "scroll",
    ];
    eventos.forEach((evento) =>
      window.addEventListener(evento, reiniciarInactividad)
    );

    timerInactividad.current = setInterval(() => {
      if (mostrarAvisoRef.current) return;
      segundosInactividad.current += 1;
      if (segundosInactividad.current >= TIEMPO_INACTIVIDAD_AVISO) {
        fechaLimite.current = Date.now() + TIEMPO_CUENTA_REGRESIVA * 1000;
        setSegundosRestantes(TIEMPO_CUENTA_REGRESIVA);
        setMostrarAviso(true);
      }
    }, 1000);

    return () => {
      eventos.forEach((evento) =>
        window.removeEventListener(evento, reiniciarInactividad)
      );
      if (timerInactividad.current) clearInterval(timerInactividad.current);
      if (timerCuentaRegresiva.current) clearInterval(timerCuentaRegresiva.current);
    };
  }, [user]);

  // Cuenta regresiva del aviso (30 segundos).
  useEffect(() => {
    if (!mostrarAviso) return undefined;

    timerCuentaRegresiva.current = setInterval(() => {
      const restante = Math.max(
        0,
        Math.ceil((fechaLimite.current - Date.now()) / 1000)
      );
      setSegundosRestantes(restante);
      if (restante <= 0) cerrarSesion();
    }, 250);

    return () => {
      if (timerCuentaRegresiva.current) clearInterval(timerCuentaRegresiva.current);
    };
  }, [mostrarAviso]);

  if (!user) return null;

  const porcentaje = (segundosRestantes / TIEMPO_CUENTA_REGRESIVA) * 100;

  return (
    <Modal
      show={mostrarAviso}
      size="md"
      aria-labelledby="aviso-sesion-title"
      centered
      backdrop="static"
      keyboard={false}
      style={{ backgroundColor: "rgba(0, 0, 0, 0.521)" }}
    >
      <Modal.Header style={{ backgroundColor: "#ffc107", color: "#212529" }}>
        <Modal.Title id="aviso-sesion-title">
          <i className="fas fa-hourglass-half mr-2"></i> Aviso de sesión
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row col-12 mx-auto text-center">
          <div className="col-12 p-2">
            <i className="fas fa-user-clock fa-5x text-warning"></i>
          </div>
          <div className="col-12 p-2">
            <h5
              style={{
                fontWeight: "bold",
                color: "#595959",
                fontFamily: "Lato,Arial,Helvetica,sans-serif",
              }}
            >
              Su sesión expirará por inactividad
            </h5>
          </div>
          <div className="col-12 p-2">
            <p style={{ fontFamily: "Lato,Arial,Helvetica,sans-serif" }}>
              No se detectó actividad en el sistema. Su sesión se cerrará
              automáticamente en{" "}
              <strong className="text-danger" style={{ fontSize: "1.6rem" }}>
                {segundosRestantes}
              </strong>{" "}
              segundos.
            </p>
          </div>
          <div className="col-12 px-4 pb-2">
            <div className="progress" style={{ height: "8px" }}>
              <div
                className="progress-bar progress-bar-striped bg-warning"
                role="progressbar"
                style={{ width: `${porcentaje}%` }}
              ></div>
            </div>
          </div>
          <div className="col-12 p-3">
            <button
              autoFocus
              className="btn btn-warning rounded-pill px-4 font-weight-bold"
              onClick={extenderSesion}
            >
              <i className="fas fa-clock mr-2"></i> Extender sesión
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SessionTimeout;
